"use client";

import { useState } from "react";
import { useFormAction, type FormAction } from "./useFormAction";
import { MapPicker } from "./MapPicker";
import {
  Banner,
  Button,
  FIELD_LABEL,
  Field,
  StepIndicator,
  ActionSubmit,
} from "./ui";
import { ArrowLeft } from "lucide-react";

/**
 * BusinessDetailsScreen (docs/ux/screens/BusinessDetailsScreen.md), shared by
 * both frontends (R22 T14).
 *
 * The second half of registering: the clinic itself, after the account. H37
 * means an **affiliated** member runs exactly this screen to register a clinic
 * of their own, because it asks for the same things and goes to the same queue;
 * what stays one per account is the registration, enforced in the data layer.
 *
 * **The two steps are hidden in opposite ways, and both are deliberate**
 * (design-system §12). Step 2 stays mounted behind a `hidden` class, because
 * unmounting it would throw away what was typed and the whole form submits at
 * once. Step 3 is mounted only when it is reached, because Leaflet cannot size
 * a map inside a hidden element and would come up as a grey box. Swapping
 * either rule for the other breaks one of them.
 */

/**
 * Registration, steps 2 and 3 of 3.
 *
 * It used to be one screen carrying four unrelated things: the business name,
 * a category grid, a full Leaflet map, and a website field. The map is a large
 * interaction and it sat in the middle of a form, so the screen read as long
 * and fiddly and the pin was easy to skip past on the way to the button.
 *
 * Splitting it costs nothing and gains a lot: **what you are** on one screen,
 * **where you are** on the next. The same submit, the same command, the same
 * fields; only the pacing changed.
 *
 * Step 2 stays mounted while hidden so its values still submit, exactly as
 * sign-up does. The **map is mounted only on step 3**, because Leaflet sizes
 * itself to its container on mount and a container that is `display: none`
 * measures zero.
 */

function checkName(value: string): string | undefined {
  const trimmed = value.trim();
  if (trimmed === "") return "Business name is required";
  if (trimmed.length > 200) return "Business name is too long";
  return undefined;
}

export function BusinessDetailsForm({
  categories,
  submit,
}: {
  categories: { id: string; name: string }[];
  /** Put the clinic in front of the Admin. The host's transport, injected. */
  submit: FormAction;
}) {
  const { state, pending, onSubmit } = useFormAction(submit);
  const [step, setStep] = useState<2 | 3>(2);

  const [businessName, setBusinessName] = useState(state.values?.businessName ?? "");
  const [picked, setPicked] = useState<string[]>([]);
  const [localErrors, setLocalErrors] = useState<{
    businessName?: string;
    categoryIds?: string;
  }>({});

  const pinError = state.fieldErrors?.latitude ?? state.fieldErrors?.longitude;

  // A server rejection on a step-2 field has to be visible, so go back to it.
  const serverStep2Error =
    state.fieldErrors?.businessName ??
    state.fieldErrors?.categoryIds ??
    state.fieldErrors?.website;
  const [lastServerError, setLastServerError] = useState(serverStep2Error);
  if (serverStep2Error !== lastServerError) {
    setLastServerError(serverStep2Error);
    if (serverStep2Error) setStep(2);
  }

  function goToStep3() {
    const errors = {
      businessName: checkName(businessName),
      categoryIds: picked.length === 0 ? "Pick at least one category" : undefined,
    };
    setLocalErrors(errors);
    if (!errors.businessName && !errors.categoryIds) setStep(3);
  }

  const toggleCategory = (id: string) =>
    setPicked((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );

  const categoryError = localErrors.categoryIds ?? state.fieldErrors?.categoryIds;

  return (
    <>
      <div className="mb-6">
        <StepIndicator current={step} total={3} />
        <h1 className="mt-4 text-2xl font-extrabold text-ink">
          {step === 2 ? "Tell us about your business" : "Where are you?"}
        </h1>
        <p className="mt-1 text-sm text-muted">
          {step === 2
            ? "What you are called, and what you offer"
            : "Drop a pin so customers can find you and search by distance"}
        </p>
      </div>

      {state.formError && <Banner>{state.formError}</Banner>}

      <form onSubmit={onSubmit}>
        <fieldset className="disabled:opacity-70">
          {/* Step 2 stays mounted when hidden so its values still submit */}
          <div className={step === 2 ? undefined : "hidden"}>
            <Field
              label="Business name"
              name="businessName"
              value={businessName}
              onChange={setBusinessName}
              error={localErrors.businessName ?? state.fieldErrors?.businessName}
              placeholder="e.g. Achrafieh Physio Center"
            />

            <div className="mb-4">
              <span className={FIELD_LABEL}>What do you offer?</span>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((category) => (
                  <label
                    key={category.id}
                    className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                      picked.includes(category.id)
                        ? "border-accent bg-accent/8 text-ink"
                        : "border-line bg-surface text-body hover:border-accent/50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="categoryIds"
                      value={category.id}
                      checked={picked.includes(category.id)}
                      onChange={() => toggleCategory(category.id)}
                      className="accent-accent"
                    />
                    {category.name}
                  </label>
                ))}
              </div>
              {categoryError && (
                <p className="mt-1.5 text-sm text-danger">{categoryError}</p>
              )}
            </div>

            <Field
              label="Website (optional)"
              name="website"
              defaultValue={state.values?.website}
              error={state.fieldErrors?.website}
              placeholder="https://…"
            />

            <Button onClick={goToStep3} className="w-full">
              Continue
            </Button>
          </div>

          {/* Step 3. The map mounts here, never hidden, so Leaflet can size it. */}
          {step === 3 && (
            <div>
              <MapPicker error={pinError} />
              <ActionSubmit pendingLabel="Submitting…" pending={pending}>
                Submit registration
              </ActionSubmit>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="mt-3 flex w-full items-center justify-center gap-1.5 text-sm font-semibold text-muted transition-colors hover:text-body"
              >
                <ArrowLeft className="size-4" aria-hidden />
                Back to your details
              </button>
            </div>
          )}
        </fieldset>
      </form>
    </>
  );
}
